/**
 * voice-v8 — Seite „Ankermomente“ (27.09.2026, Ticket 2637).
 *
 * Martin am 26.09., nachdem er den Anker „Selbstbewusstsein“ angelegt hatte:
 * ein eigener Eintrag in der Seitenleiste mit ALLEN Ankern auf einen Blick —
 * Bild und Text, mobil gut lesbar, Bild antippen = groß ansehen. Freigegeben
 * am 27.09.: am Telefon die Liste (Entwurf A), am Rechner die Tabelle.
 *
 * ⚠️ Nur, was im Speicher steht: Bild, Gefühl, Situation, Ort, erlebt am. Eine
 *    Verknüpfung zur tiefen Frage gibt es in `ankermomente` nicht — darum
 *    auch keine Spalte dafür.
 * ⚠️ „Erlebt am“ ist der Tag des Moments. Fehlt er, fehlt er: in Liste und
 *    Tabelle entfällt der Teil (fehlt auch der Ort, steht in der Tabelle
 *    „—“). Nie durch das Datum der Eintragung ersetzen (die Route liefert es
 *    gar nicht aus).
 * ⚠️ Nur ansehen. Verstecken und Bearbeiten laufen im Gespräch
 *    (anker_bearbeiten) — ein Löschknopf hier widerspräche anker-store.ts.
 */
import { kalenderTag } from "../../shared/spiegel-zeitleiste.js";
import { openGallery, type GalleryItem } from "./lightbox.js";
import { clearPageHash, setPageHash } from "./page-routes.js";
import { buildLoadingHint, buildPageHeader } from "./sheet-ui.js";

interface AnkerEintrag {
  id: string;
  bildUrl: string;
  gefuehl: string;
  situation: string;
  ort: string | null;
  erlebtAm: string | null;
}

/**
 * Ab hier die Tabelle, darunter die Liste.
 *
 * 29.09.2026 (Ticket 2792, Martin: „am Rechner zusammengestaucht, die Spalte
 * Situation zu eng“): Gemessen bei 1280 blieben der Situation 304 px — die
 * festen Spalten allein nahmen 594 px der 900er Lesespalte; neben der
 * offenen Seitenleiste bei 1024 wären es rechnerisch 78 px gewesen. Jetzt
 * teilen sich Datum und Ort eine Spalte (wie „Datum · Ort“ in der Liste),
 * und die Tabelle kommt erst ab 1200. Gemessen: bei 1280 hat die Situation
 * 434 px, bei 1200 (Spalte 848) 382 px. Darunter die Liste.
 */
const TABELLE_AB = "(min-width: 1200px)";

/**
 * Die Vorschau statt des Originals (Ticket 2792). Martin: „die Vorschaubilder
 * laden ewig, es werden die vollen Bilder geholt“ — gemessen 0,3–1,1 MB je
 * Anker für einen Rahmen von 68 px. `/thumb` (server/thumb.ts) rechnet
 * einmal auf 320 px herunter und legt das Ergebnis ab: im Mittel 20 KB.
 * 320 reicht für 68 px auf einem Dreifach-Bildschirm auch im Querformat.
 * Groß (Bildbetrachter) bleibt das Original.
 */
function vorschauUrl(bildUrl: string): string {
  return bildUrl.startsWith("/uploads/")
    ? `/thumb?p=${encodeURIComponent(bildUrl)}&w=320`
    : bildUrl;
}

/** Das Bildrahmen-Zeichen aus dem Entwurf — statt eines kaputten Bildes. */
const BILD_ZEICHEN =
  '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" fill="none" ' +
  'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<rect x="3.5" y="4.5" width="17" height="15" rx="2.5"/><circle cx="9" cy="10" r="1.8"/>' +
  '<path d="M4 17l5-4.5 4 3.5 3-2.5 4.5 3.5"/></svg>';

let aufraeumen: (() => void) | null = null;

export function closeAnkerView(): void {
  aufraeumen?.();
  aufraeumen = null;
  document.getElementById("anker-sheet")?.remove();
  clearPageHash("anker");
}

async function ladeAnker(): Promise<AnkerEintrag[] | null> {
  try {
    const r = await fetch("/api/anker", { credentials: "same-origin" });
    if (!r.ok) return null;
    const d = (await r.json()) as { ok?: boolean; anker?: AnkerEintrag[] };
    return d.ok && Array.isArray(d.anker) ? d.anker : null;
  } catch {
    return null;
  }
}

/** „26. Sept. 2026 · Arugam Bay“ — nur die Teile, die es gibt. */
export function ankerNebenzeile(a: AnkerEintrag): string {
  return [kalenderTag(a.erlebtAm, "lang"), a.ort?.trim() || null]
    .filter((t): t is string => !!t)
    .join(" · ");
}

/** `kante` = Seitenlänge des Rahmens in CSS-Pixeln (.anker-bild). */
function bildRahmen(a: AnkerEintrag, klasse: string, kante: number): HTMLElement {
  const rahmen = document.createElement("span");
  rahmen.className = klasse;
  const img = document.createElement("img");
  img.src = vorschauUrl(a.bildUrl);
  img.alt = a.gefuehl;
  img.width = kante;
  img.height = kante;
  img.loading = "lazy";
  img.decoding = "async";
  img.addEventListener("error", () => {
    rahmen.classList.add("anker-bild--fehlt");
    rahmen.innerHTML = BILD_ZEICHEN;
  });
  rahmen.appendChild(img);
  return rahmen;
}

function zeigeGross(anker: AnkerEintrag[], idx: number): void {
  const items: GalleryItem[] = anker.map((a) => ({
    id: a.id,
    url: a.bildUrl,
    mime: "image/*",
    caption: a.situation.trim() ? `${a.gefuehl} — ${a.situation.trim()}` : a.gefuehl,
    source: "user",
    createdAt: "",
  }));
  openGallery(items, idx);
}

function leiserSatz(text: string, zusatz?: string): HTMLElement {
  const box = document.createElement("div");
  box.className = "anker-leer";
  const p = document.createElement("p");
  p.className = "anker-leer-satz";
  p.textContent = text;
  box.appendChild(p);
  if (zusatz) {
    const z = document.createElement("p");
    z.className = "anker-leer-zusatz";
    z.textContent = zusatz;
    box.appendChild(z);
  }
  return box;
}

/** Telefon (Entwurf A): eine Karte, je Anker eine Zeile als Knopf. */
function baueListe(anker: AnkerEintrag[]): HTMLElement[] {
  const intro = document.createElement("p");
  intro.className = "anker-intro";
  intro.textContent = "Jeder Anker mit seinem Bild. Antippen öffnet das Bild groß.";

  const ul = document.createElement("ul");
  ul.className = "anker-liste";
  ul.setAttribute("aria-label", "Ankermomente");
  anker.forEach((a, i) => {
    const li = document.createElement("li");
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "anker-zeile";
    knopf.setAttribute("aria-label", `Anker ${a.gefuehl} – Bild groß ansehen`);

    const text = document.createElement("span");
    text.className = "anker-zeile-text";
    // Der Name des Knopfs ist die Handlung; Situation und Datum liest der
    // Vorleser als Beschreibung dazu, statt sie zu verschlucken.
    text.id = `anker-text-${i}`;
    knopf.setAttribute("aria-describedby", text.id);
    const name = document.createElement("span");
    name.className = "anker-name";
    name.textContent = a.gefuehl;
    text.appendChild(name);
    if (a.situation.trim()) {
      const s = document.createElement("span");
      s.className = "anker-situation";
      s.textContent = a.situation.trim();
      text.appendChild(s);
    }
    const neben = ankerNebenzeile(a);
    if (neben) {
      const m = document.createElement("span");
      m.className = "anker-meta";
      m.textContent = neben;
      text.appendChild(m);
    }

    knopf.append(bildRahmen(a, "anker-bild", 68), text);
    knopf.addEventListener("click", () => zeigeGross(anker, i));
    li.appendChild(knopf);
    ul.appendChild(li);
  });
  return [intro, ul];
}

/**
 * „Wann & wo“: erlebt am über dem Ort — nur, was es gibt; fehlt beides, „—“.
 * (Ticket 2792: vorher je eine eigene Spalte, meist mit einem „—“ darin.)
 */
function wannWoZelle(a: AnkerEintrag): HTMLElement {
  const td = document.createElement("td");
  td.className = "anker-zelle-leise";
  const wann = kalenderTag(a.erlebtAm, "zahlen");
  const wo = a.ort?.trim() || null;
  if (!wann && !wo) {
    td.textContent = "—";
    return td;
  }
  for (const [text, klasse] of [
    [wann, "anker-wann"],
    [wo, "anker-wo"],
  ] as const) {
    if (!text) continue;
    const s = document.createElement("span");
    s.className = klasse;
    s.textContent = text;
    td.appendChild(s);
  }
  return td;
}

/** Rechner: Bild | Anker | Situation | Wann & wo. */
function baueTabelle(anker: AnkerEintrag[]): HTMLElement[] {
  const tafel = document.createElement("div");
  tafel.className = "anker-tafel";
  const tabelle = document.createElement("table");
  tabelle.className = "anker-tabelle";
  const kopfzeile = document.createElement("tr");
  for (const [titel, klasse] of [
    ["Bild", "anker-sp-bild"],
    ["Anker", "anker-sp-name"],
    ["Situation", "anker-sp-situation"],
    ["Wann & wo", "anker-sp-wannwo"],
  ] as const) {
    const th = document.createElement("th");
    th.scope = "col";
    th.className = klasse;
    th.textContent = titel;
    kopfzeile.appendChild(th);
  }
  const thead = document.createElement("thead");
  thead.appendChild(kopfzeile);

  const tbody = document.createElement("tbody");
  anker.forEach((a, i) => {
    const tr = document.createElement("tr");
    const bildZelle = document.createElement("td");
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "anker-bildknopf";
    knopf.setAttribute("aria-label", `Bild von ${a.gefuehl} groß ansehen`);
    knopf.appendChild(bildRahmen(a, "anker-bild anker-bild--tabelle", 64));
    knopf.addEventListener("click", () => zeigeGross(anker, i));
    bildZelle.appendChild(knopf);

    const name = document.createElement("td");
    name.className = "anker-name";
    name.textContent = a.gefuehl;
    // Die Kürzung auf vier Zeilen sitzt am Inneren — eine Zelle mit
    // eigener Anzeigeart fiele aus der Tabelle.
    const situation = document.createElement("td");
    const satz = document.createElement("span");
    satz.className = "anker-situation";
    satz.textContent = a.situation.trim();
    situation.appendChild(satz);
    tr.append(bildZelle, name, situation, wannWoZelle(a));
    tbody.appendChild(tr);
  });
  tabelle.append(thead, tbody);
  tafel.appendChild(tabelle);
  return [tafel];
}

/** Zeichnet die Anker in die Form, die zur Breite passt — dieselben Daten. */
function zeichneAnker(body: HTMLElement, anker: AnkerEintrag[], tabelle: boolean): void {
  // Auch die Tabelle bleibt in der einen Lesespalte (900) — gemessen passt
  // sie, und so fluchtet sie mit dem Kopf wie jede andere Seite.
  body.replaceChildren(...(tabelle ? baueTabelle(anker) : baueListe(anker)));
}

export async function openAnkerView(): Promise<void> {
  if (document.getElementById("anker-sheet")) return;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));

  const el = document.createElement("div");
  el.className = "sheet page-sheet anker-sheet";
  el.id = "anker-sheet";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Ankermomente");
  const card = document.createElement("div");
  card.className = "sheet-card";
  const body = document.createElement("div");
  body.className = "anker-body page-body";
  const kopf = buildPageHeader("Ankermomente", closeAnkerView, { subtitle: "Lade …" });
  card.append(kopf, body);
  el.appendChild(card);
  document.body.appendChild(el);
  setPageHash("anker");

  body.appendChild(buildLoadingHint("Lade Ankermomente …"));
  const anker = await ladeAnker();
  // In der Zwischenzeit geschlossen (oder neu geöffnet): nichts anfassen.
  if (!el.isConnected) return;

  const untertitel = kopf.querySelector<HTMLElement>(".hd-subtitle");
  if (anker === null) {
    untertitel?.remove();
    body.replaceChildren(
      leiserSatz("Die Ankermomente ließen sich gerade nicht laden.", "Bitte später noch einmal öffnen."),
    );
    return;
  }
  if (anker.length === 0) {
    untertitel?.remove();
    body.replaceChildren(
      leiserSatz(
        "Noch keine Ankermomente festgehalten.",
        "Ein Ankermoment ist ein Bild mit dem Gefühl, das es trägt, und der Situation dazu. " +
          "Erzähl im Gespräch von so einem Moment — dann steht er hier.",
      ),
    );
    return;
  }
  if (untertitel) untertitel.textContent = `${anker.length} Anker`;

  const breite = window.matchMedia?.(TABELLE_AB) ?? null;
  const zeichne = (): void => zeichneAnker(body, anker, !!breite?.matches);
  zeichne();
  if (breite?.addEventListener) {
    breite.addEventListener("change", zeichne);
    aufraeumen = () => breite.removeEventListener("change", zeichne);
  }
}
